/**
 * What the Cal.com dialog checks before it sends anything: every rule the
 * `cal-integration` function (supabase/functions/cal-integration/validation.ts)
 * runs on a connection, in the order it runs them, so a value it would refuse
 * never leaves the dialog and the reason shows under the field to change, in
 * words. The Angular dashboard says the same
 * (`features/assistant/forms/cal-integration.ts`).
 */

import { FIELD_REFUSAL_PATTERN, sentence, UNREACHABLE } from "./refusal";

/** A field of the Cal.com dialog. */
export type CalField = "apiKey" | "eventTypeId";

/** The dialog's fields as typed. */
export type CalForm = Record<CalField, string>;

/** Each field's error; a field that is fine has none. */
export type CalErrors = Partial<Record<CalField, string>>;

/** The function's limits, counted as it counts them: on the trimmed value. */
const KEY_MAX_LENGTH = 255;
/** `cal_integrations.cal_event_type_id` is a Postgres `integer`. */
const EVENT_TYPE_ID_MAX = 2_147_483_647;

export const CAL_MESSAGES = {
  keyRequired: "Paste your Cal.com API key.",
  keyTooLong: `A Cal.com API key is at most ${KEY_MAX_LENGTH} characters.`,
  keySpaces: "A Cal.com API key has no spaces.",
  keyPrefix: "A Cal.com API key starts with cal_.",
  /** Under an edit's key box, which starts empty: the stored key is never read back. */
  keyKept: "Leave blank to keep the current key.",
  eventTypeRequired: "Enter the event type ID.",
  eventTypeWhole: "The event type ID is a whole number above 0.",
  eventTypeTooLarge: "That event type ID is too large.",
} as const;

export { UNREACHABLE };

function keyError(key: string, connecting: boolean): string | undefined {
  if (!key) return connecting ? CAL_MESSAGES.keyRequired : undefined;
  if (key.length > KEY_MAX_LENGTH) return CAL_MESSAGES.keyTooLong;
  if (/\s/.test(key)) return CAL_MESSAGES.keySpaces;
  return key.startsWith("cal_") ? undefined : CAL_MESSAGES.keyPrefix;
}

function eventTypeError(id: string): string | undefined {
  if (!id) return CAL_MESSAGES.eventTypeRequired;
  if (!/^\d+$/.test(id) || Number(id) < 1) return CAL_MESSAGES.eventTypeWhole;
  return Number(id) > EVENT_TYPE_ID_MAX ? CAL_MESSAGES.eventTypeTooLarge : undefined;
}

/**
 * Both fields are checked trimmed, as the server checks them. Connecting needs
 * a key; an edit's empty key box keeps the stored key, so it is no error there.
 */
export const calErrors = (form: CalForm, connecting: boolean): CalErrors => ({
  apiKey: keyError(form.apiKey.trim(), connecting),
  eventTypeId: eventTypeError(form.eventTypeId.trim()),
});

/**
 * A refusal from the function as the dialog shows it: under `field` while that
 * field still holds what was `sent`, or for the whole dialog when it is about
 * no field of the dialog.
 */
export interface CalRefusal {
  field?: CalField;
  sent?: string;
  message: string;
}

/** The body fields the function names in a `"<field>: <reason>"` refusal, and the dialog field each one is. */
const SERVER_FIELDS: Record<string, CalField> = { cal_api_key: "apiKey", cal_event_type_id: "eventTypeId" };

/**
 * A failed request in the dialog's words, for `sent` — the dialog's fields as
 * they were sent, or none for a button with no fields. What `calErrors`
 * mirrors never gets here; what does is what only the server and Cal.com know:
 * a key or an event type Cal.com refused (`"cal_api_key: Cal.com did not
 * accept this API key"` goes under the key as "Cal.com did not accept this API
 * key."), an agent another tab connected meanwhile, a role, Cal.com out of
 * reach. A `field: reason` string is never shown as it came.
 */
export function calRefusalOf(error: unknown, fallback: string, sent?: CalForm): CalRefusal {
  const { status, message } = (error ?? {}) as { status?: unknown; message?: unknown };
  // A failure the server answered carries its HTTP status (`ApiError`).
  if (typeof status !== "number") return { message: UNREACHABLE };
  const text = typeof message === "string" && message ? message : fallback;
  const match = FIELD_REFUSAL_PATTERN.exec(text);
  if (!match) return { message: sentence(text) };
  const field = SERVER_FIELDS[match[1]];
  const reason = sentence(match[2]);
  return field && sent ? { field, sent: sent[field], message: reason } : { message: reason };
}

/** The field a refusal is about, while it still holds what was sent: changing it clears the error. */
export const refusedCalFields = (refusal: CalRefusal | undefined, form: CalForm): CalErrors =>
  refusal?.field && form[refusal.field] === refusal.sent ? { [refusal.field]: refusal.message } : {};
